import { cva, type VariantProps } from 'class-variance-authority';

const defaultKundeBtnClasses = [
  'kunde:text-sm',
  'kunde:rounded-md',
  'kunde:focus-visible:outline-none',
  'kunde:focus-visible:ring-2',
  'kunde:focus-visible:ring-blue-60',
  'kunde:focus-visible:border-transparent',
];

export const buttonVariants = cva(
  [
    'flex items-center rounded border font-medium',
    'disabled:cursor-default enabled:cursor-pointer',
    'laane:disabled:cursor-not-allowed',
    ...defaultKundeBtnClasses,
  ],
  {
    variants: {
      theme: {
        default: [
          'border-transparent text-neutral-110 disabled:text-neutral-60',
          'bg-neutral-10 hover:bg-neutral-40 focus:bg-neutral-40 active:animate-active active:bg-neutral-10',
          'aria-expanded:animate-active aria-expanded:bg-neutral-10',
          'kunde:border-neutral-40 kunde:bg-transparent kunde:text-neutral-200 kunde:hover:bg-other-5/10 kunde:active:bg-other-60/40 kunde:disabled:border-neutral-10 kunde:disabled:text-neutral-60',
          'kunde:enabled:aria-expanded:bg-blue-10 kunde:enabled:aria-expanded:border-blue-10',
        ],
        primary: [
          'border-transparent text-white disabled:text-neutral-60',
          'bg-blue-100 hover:bg-blue-40 focus:bg-blue-40 active:bg-blue-100 disabled:bg-neutral-10',
          'enabled:aria-expanded:bg-blue-100',
          'kunde:hover:bg-blue-60 kunde:focus:border-blue-10 kunde:focus:bg-blue-100 kunde:active:bg-blue-110 kunde:disabled:bg-neutral-10 kunde:aria-disabled:bg-neutral-10 kunde:disabled:text-neutral-60 kunde:aria-disabled:text-neutral-60',
        ],
        secondary: [
          'border-blue-60 text-blue-60 focus-visible:border-transparent',
          'hover:bg-blue-5 enabled:active:bg-blue-10 disabled:border-transparent disabled:bg-neutral-10 disabled:text-neutral-60',
          'kunde:border-blue-10 kunde:bg-teal-5 kunde:text-blue-100 kunde:hover:border-blue-20 kunde:hover:bg-blue-10 kunde:active:border-transparent kunde:active:bg-blue-20 kunde:disabled:border-transparent kunde:disabled:bg-neutral-10 kunde:disabled:text-neutral-60',
        ],
        tertiary: [
          'border-transparent text-blue-60',
          'hover:bg-blue-5 active:bg-blue-5 disabled:bg-transparent disabled:text-neutral-60',
          'kunde:bg-white kunde:hover:bg-blue-5 kunde:active:bg-blue-5 kunde:disabled:bg-white',
        ],
        subtle: [
          'border-transparent text-neutral-110',
          'focus:bg-neutral-10 enabled:hover:bg-neutral-10 disabled:bg-transparent disabled:text-neutral-60',
          'kunde:bg-white kunde:text-neutral-200 kunde:hover:bg-[#172975]/10 kunde:active:bg-[#858AA1]/40 kunde:disabled:bg-white kunde:disabled:text-neutral-60',
        ],
        ghost: [
          'border-transparent text-neutral-200',
          'focus:bg-neutral-10 enabled:hover:bg-neutral-10 disabled:bg-transparent disabled:text-neutral-60',
          'kunde:bg-white kunde:text-neutral-200 kunde:hover:bg-[#172975]/10 kunde:active:bg-[#858AA1]/40 kunde:disabled:bg-white kunde:disabled:text-neutral-60',
        ],
        warning: [
          'border-transparent text-neutral-110 enabled:active:text-blue-110 disabled:text-neutral-60',
          'bg-orange-100 enabled:hover:bg-orange-60 enabled:active:bg-orange-110 disabled:bg-neutral-10',
          'kunde:text-neutral-200 kunde:active:text-neutral-200 kunde:disabled:text-neutral-60',
        ],
        danger: [
          'border-transparent text-white hover:text-neutral-200 disabled:text-neutral-60',
          'bg-red-100 enabled:hover:bg-red-60 enabled:active:bg-red-110 enabled:active:text-white disabled:bg-neutral-10',
        ],
        none: 'group border-0 p-1',
      },
      inverted: {
        true: '',
        false: '',
      },
      compact: {
        true: 'text-xs leading-3 laane:h-6 kunde:h-10',
        false: 'laane:text-xs font-medium leading-4 kunde:h-14',
      },
      rightAligned: {
        true: 'flex-row-reverse',
        false: '',
      },
      loading: {
        true: 'p-4 sm:p-2 md:px-4',
        false: '',
      },
      hasIcon: {
        true: '',
        false: '',
      },
      hasLabel: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        theme: 'default',
        inverted: true,
        className: [
          'kunde:bg-transparent kunde:text-blue-10 kunde:border-blue-10 kunde:enabled:hover:bg-other-60/40',
          'kunde:focus-visible:ring-blue-20 kunde:focus-visible:border-blue-20 kunde:enabled:active:bg-other-5/5 kunde:disabled:border-neutral-100 kunde:disabled:text-neutral-100',
        ],
      },
      {
        theme: 'primary',
        inverted: true,
        className: [
          'kunde:bg-blue-10 kunde:text-blue-100 kunde:hover:bg-blue-20 kunde:focus:border-blue-110 kunde:focus:bg-blue-10 ',
          'kunde:disabled:bg-other-100/60 kunde:disabled:text-neutral-60 kunde:focus:ring-blue-20 kunde:active:bg-blue-40',
        ],
      },
      {
        theme: 'secondary',
        inverted: true,
        className: [
          'kunde:bg-blue-60 kunde:text-blue-5 kunde:hover:bg-text-blue-10 kunde:hover:bg-blue-100',
          'kunde:focus-visible:border-blue-110 kunde:focus-visible:ring-blue-20 kunde:focus:text-blue-5 kunde:focus:bg-blu-60',
          'kunde:enabled:active:bg-blue-100 kunde:enabled:active:text-blue-20',
          'kunde:disabled:bg-other-100/60 kunde:disabled:text-neutral-60',
        ],
      },
      {
        theme: 'tertiary',
        inverted: true,
        className: [
          'kunde:text-blue-10 kunde:hover:bg-other-60/40 kunde:focus-visible:ring-blue-20 kunde:focus-visible:bg-blue-110',
          'kunde:enabled:active:bg-other-60/40 kunde:enabled:active:text-blue-20',
          'kunde:disabled:bg-transparent kunde:disabled:text-neutral-100',
        ],
      },
      {
        theme: 'subtle',
        inverted: true,
        className: [
          'kunde:text-white kunde:hover:bg-other-60/40 kunde:focus-visible:ring-blue-20 kunde:focus-visible:bg-blue-110',
          'kunde:enabled:active:bg-other-60/40 kunde:enabled:active:text-blue-10 kunde:disabled:bg-transparent kunde:disabled:text-neutral-100',
        ],
      },
      {
        theme: 'warning',
        inverted: true,
        className: [
          'kunde:bg-orange-10 kunde:text-neutral-200 kunde:hover:bg-orange-40 kunde:focus-visible:orange-10 ',
          'kunde:focus-visible:border-blue-20 kunde:focus-visible:ring-blue-20 kunde:enabled:active:bg-orange-100',
          'kunde:disabled:bg-other-100/60 kunde:disabled:text-neutral-60',
        ],
      },
      {
        theme: 'danger',
        inverted: true,
        className: [
          'kunde:bg-red-10 kunde:text-red-110 kunde:hover:bg-red-40 kunde:focus-visible:ring-blue-20 kunde:focus-visible:border-blue-20',
          'kunde:disabled:bg-other-100/60 kunde:disabled:text-neutral-60 kunde:enabled:active:bg-red-100 kunde:enabled:active:text-neutral-200',
        ],
      },
      {
        compact: true,
        loading: false,
        rightAligned: true,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-2 laane:pr-1 kunde:px-4',
      },
      {
        compact: true,
        loading: false,
        rightAligned: false,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-1 laane:pr-2 kunde:px-4',
      },
      {
        compact: false,
        loading: false,
        rightAligned: true,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-4 laane:pr-2 kunde:pl-4 kunde:pr-2',
      },
      {
        compact: false,
        loading: false,
        rightAligned: false,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-2 laane:pr-4 kunde:px-6',
      },
      {
        compact: false,
        loading: false,
        rightAligned: false,
        hasIcon: true,
        hasLabel: true,
        className: 'laane:pl-2 laane:pr-4 kunde:px-6',
      },
      {
        compact: false,
        loading: false,
        hasIcon: false,
        hasLabel: true,
        className: 'laane:px-4 laane:py-1 kunde:px-4 kunde:py-4 kunde:px-6',
      },
      {
        compact: true,
        loading: false,
        hasIcon: false,
        className: 'laane:px-2 kunde:px-4 kunde:py-2',
      },
      {
        compact: true,
        loading: true,
        className: 'laane:px-2 laane:py-1 kunde:px-4 kunde:py-2',
      },
      {
        compact: false,
        loading: true,
        className: 'laane:px-4 laane:py-1 kunde:px-4',
      },
    ],
    defaultVariants: {
      theme: 'default',
      compact: false,
      rightAligned: false,
      loading: false,
      hasIcon: false,
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
